import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FileText, AlertCircle, Sparkles, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ReportsFilterBar, type ReportsFilterState } from '@/components/reports/reports-filter-bar';
import { ReportCard } from '@/components/reports/report-card';
import { ReportCreateModal } from '@/components/reports/report-create-modal';
import { ReportEditModal } from '@/components/reports/report-edit-modal';
import { ShareDialog } from '@/components/report/share-dialog';
import { Pagination } from '@/components/pagination';
import { useDebounced } from '@/hooks/use-debounced';
import {
  listReports,
  deleteReport,
  getAvailablePeriods,
  getReport,
  isAllBrand,
  type AvailablePeriods,
  type BrandStat,
  type Meta,
  type Report,
  type ReportListItem,
  type ReportStatus,
} from '@/lib/api';

const PAGE_LIMIT = 24;

export interface ReportsListRouteProps {
  brands: BrandStat[];
}

export default function ReportsListRoute({ brands }: ReportsListRouteProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter: ReportsFilterState = useMemo(
    () => ({
      brand: searchParams.get('brand') ?? '',
      period: searchParams.get('period') ?? '',
      status: (searchParams.get('status') as ReportStatus | null) ?? '',
      search: searchParams.get('search') ?? '',
    }),
    [searchParams]
  );
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [meta, setMeta] = useState<Meta>({ total: 0, page: 1, limit: PAGE_LIMIT, pages: 1 });
  const [available, setAvailable] = useState<AvailablePeriods | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<ReportListItem | null>(null);
  const [editTarget, setEditTarget] = useState<Report | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [shareTarget, setShareTarget] = useState<ReportListItem | null>(null);

  const debouncedSearch = useDebounced(filter.search, 300);

  // Load available periods once
  useEffect(() => {
    let cancelled = false;
    getAvailablePeriods()
      .then((p) => !cancelled && setAvailable(p))
      .catch((err) => console.error('Periods load failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load list when filter or page changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listReports({
      brand: filter.brand || undefined,
      status: filter.status || undefined,
      period: filter.period || undefined,
      page,
      limit: PAGE_LIMIT,
    })
      .then(({ data, meta }) => {
        if (cancelled) return;
        // Client-side search filter (server doesn't support yet)
        const filtered = debouncedSearch
          ? data.filter(
              (r) =>
                r.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                (r.description ?? '').toLowerCase().includes(debouncedSearch.toLowerCase())
            )
          : data;
        setReports(filtered);
        setMeta(meta);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setReports([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [filter.brand, filter.status, filter.period, debouncedSearch, page, reloadKey]);

  const updateParams = useCallback(
    (patch: Partial<ReportsFilterState> & { page?: number }) => {
      const next = new URLSearchParams(searchParams);
      const map: Record<string, string | undefined> = {
        brand: patch.brand ?? filter.brand,
        period: patch.period ?? filter.period,
        status: patch.status ?? filter.status,
        search: patch.search ?? filter.search,
      };
      for (const [key, value] of Object.entries(map)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      const newPage = patch.page ?? 1;
      if (newPage > 1) next.set('page', String(newPage));
      else next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [filter, searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (patch: Partial<ReportsFilterState>) => {
      updateParams({ ...patch, page: 1 });
    },
    [updateParams]
  );

  const handleReset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      updateParams({ page: nextPage });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [updateParams]
  );

  const handleDeleteRequest = useCallback((report: ReportListItem) => {
    setDeleteTarget(report);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteReport(deleteTarget.id);
      setReports((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success(`Đã xóa báo cáo "${deleteTarget.title}"`, {
        description: 'Các link share liên quan đã bị vô hiệu hóa.',
      });
    } catch (err) {
      toast.error('Không xóa được báo cáo', {
        description: (err as Error).message,
      });
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const handleCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleCreated = useCallback(() => {
    setReloadKey((k) => k + 1);
    toast.success('Đã tạo báo cáo mới', {
      description: 'Snapshot data đã được frozen tại thời điểm này.',
    });
    if (page > 1) {
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
  }, [page, searchParams, setSearchParams]);

  const handleEdit = useCallback(async (report: ReportListItem) => {
    setEditLoading(true);
    try {
      const full = await getReport(report.id);
      setEditTarget(full);
    } catch (err) {
      toast.error('Không tải được báo cáo', { description: (err as Error).message });
    } finally {
      setEditLoading(false);
    }
  }, []);

  const handleEditSaved = useCallback(() => {
    toast.success('Đã lưu thay đổi');
    setReloadKey((k) => k + 1);
  }, []);

  const handleShare = useCallback((report: ReportListItem) => {
    // Always open dialog — `createDisabledReason` chặn việc tạo link mới nhưng cho admin
    // vẫn revoke link cũ trong trường hợp report bị đổi sang draft sau khi đã share.
    setShareTarget(report);
  }, []);

  const shareTargetBlock = useMemo(() => {
    if (!shareTarget) return null;
    if (isAllBrand(shareTarget.brand)) return 'Báo cáo Toàn ngành không share được cho partner — aggregate cross-brand chỉ admin xem nội bộ.';
    if (shareTarget.status === 'draft') return 'Phải Publish trước khi tạo link share — đổi status trong Chỉnh sửa.';
    return null;
  }, [shareTarget]);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Báo cáo Hãng</h1>
          <p className="text-sm text-muted-foreground">
            Quản lý báo cáo theo hãng × period. Snapshot frozen tại create time.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          Tạo báo cáo mới
        </Button>
      </header>

      <ReportsFilterBar
        state={filter}
        brands={brands}
        available={available}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lỗi tải danh sách</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-3 rounded-xl border bg-card p-4">
              <div className="flex gap-1.5">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-20" />
                <Skeleton className="ml-auto h-5 w-16" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-2/3" />
              <div className="flex justify-between border-t pt-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : reports.length === 0 ? (
        (() => {
          const hasFilter = Boolean(filter.brand || filter.period || filter.status || filter.search);
          // Filtered no-match → minimal hint. First-time (no filter) → onboarding CTA.
          if (hasFilter) {
            return (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card py-16 text-center">
                <div className="rounded-full bg-muted p-3 text-muted-foreground">
                  <Search className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Không tìm thấy báo cáo nào</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Thử bỏ filter hoặc đổi từ khóa tìm kiếm.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <Filter className="h-4 w-4" />
                  Xóa tất cả filter
                </Button>
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed bg-gradient-to-b from-primary/5 to-card py-12 text-center sm:py-16">
              <div className="rounded-full bg-primary/10 p-4 text-primary">
                <FileText className="h-7 w-7" />
              </div>
              <div className="max-w-md space-y-1.5 px-4">
                <h3 className="text-lg font-semibold">Tạo báo cáo đầu tiên cho hãng</h3>
                <p className="text-sm text-muted-foreground">
                  Báo cáo gắn với 1 hãng × 1 period — snapshot data sẽ được frozen tại thời điểm tạo
                  để partner xem nhất quán dù sales sau đó có đổi.
                </p>
              </div>
              <ul className="grid w-full max-w-md gap-2 px-6 text-left text-xs text-muted-foreground sm:grid-cols-3">
                <li className="flex items-start gap-1.5 rounded-md border bg-background/50 p-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>Auto-suggest title từ brand × period</span>
                </li>
                <li className="flex items-start gap-1.5 rounded-md border bg-background/50 p-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>Template Hãng / Sale / Nội bộ — auto-hide cột nội bộ</span>
                </li>
                <li className="flex items-start gap-1.5 rounded-md border bg-background/50 p-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>QoQ compare + Top SKU + Spec breakdown built-in</span>
                </li>
              </ul>
              <Button onClick={handleCreate} size="lg" className="mt-1">
                <Plus className="h-4 w-4" />
                Tạo báo cáo mới
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reports.map((r) => (
              <ReportCard
                key={r.id}
                report={r}
                onEdit={handleEdit}
                onShare={handleShare}
                onDelete={handleDeleteRequest}
              />
            ))}
          </div>
          <Pagination meta={meta} pageSize={PAGE_LIMIT} onPageChange={handlePageChange} />
        </>
      )}

      <ReportCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        brands={brands}
        available={available}
        onCreated={handleCreated}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={deleteTarget ? `Xóa báo cáo "${deleteTarget.title}"?` : ''}
        description={
          <>
            Hành động <strong className="text-destructive">không thể hoàn tác</strong>. Các link share liên quan
            sẽ bị vô hiệu hóa ngay lập tức.
          </>
        }
        confirmText="Xóa báo cáo"
        cancelText="Hủy"
        variant="destructive"
        onConfirm={confirmDelete}
      />

      <ReportEditModal
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        report={editTarget}
        onSaved={handleEditSaved}
      />

      {shareTarget && (
        <ShareDialog
          open={!!shareTarget}
          onOpenChange={(o) => !o && setShareTarget(null)}
          report={{ id: shareTarget.id, title: shareTarget.title, brand: shareTarget.brand }}
          createDisabledReason={shareTargetBlock}
        />
      )}

      {editLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/40 backdrop-blur-sm">
          <div className="rounded-md border bg-card px-4 py-2 text-sm shadow-md">
            Đang tải báo cáo...
          </div>
        </div>
      )}
    </div>
  );
}
